import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { MultiSelect, type MultiOption } from "@/components/ui/multi-select";
import type { Campaign, Firm } from "@/lib/types";

/**
 * Making one path per firm or per campaign (ACL-339, ACL-340).
 *
 * The questions themselves stay generic: one flow, and the paths vary by who the lead belongs
 * to. A campaign is firm, state and language together, so it is the coarsest of the scopes; the
 * firm is the usual one. The accident's state is an ordinary question, so varying by it is
 * branching on that answer rather than anything here.
 *
 * **It opens on what the step already varies by.** Reopening it used to show an empty list however
 * many paths the step had, so an admin adding a fourth firm had to remember and retype the three
 * that were there, and pressing the button replaced their paths with one. The pick is the whole
 * set, and the dialog says what will go as well as what will come.
 *
 * The list is a `MultiSelect`, not a column of checkboxes: it searches once a tenant has more
 * than a handful of campaigns, which is the case the plain list was unusable in — thirty rows
 * with no search, no All, and no sense of how many were ticked.
 */
export interface VaryByCurrent {
  source: "firm" | "campaign";
  /** The ids already picked, as the branches' own conditions hold them. */
  ids: string[];
}

export function VaryByDialog({
  open,
  onOpenChange,
  firms,
  campaigns,
  current,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tenant's own firms and campaigns, as the Firms and Campaigns pages read them. */
  firms: Firm[];
  campaigns: Campaign[];
  /** What this step varies by today, or null for a step that varies by nothing yet. */
  current?: VaryByCurrent | null;
  onPick: (source: "firm" | "campaign", picked: { id: string | number; label: string }[]) => void;
}) {
  const [source, setSource] = React.useState<"firm" | "campaign">("firm");
  const [chosen, setChosen] = React.useState<string[]>([]);

  // Open on what is there. A step that varies by nothing yet opens on firms, which is the usual
  // scope; one that already fans out opens on its own source with its own ids ticked.
  React.useEffect(() => {
    if (!open) return;
    setSource(current?.source ?? "firm");
    setChosen(current?.ids ?? []);
  }, [open, current?.source, current?.ids?.join(",")]);

  const options: MultiOption[] = React.useMemo(
    () =>
      source === "firm"
        ? firms.map((f) => ({
            value: String(f.id),
            label: f.label || f.name || f.code,
            hint: f.states.length ? f.states.join(", ") : "no states listed",
            keywords: [f.code, f.name].filter(Boolean).join(" "),
          }))
        : campaigns.map((c) => ({
            value: c.id,
            label: c.name,
            hint: [c.state, c.language].filter(Boolean).join(" · ") || c.slug,
            keywords: c.slug,
          })),
    [source, firms, campaigns],
  );

  const known = new Set(options.map((one) => one.value));
  // An id whose firm or campaign has since been archived: it still steers a branch, so it is
  // kept rather than quietly dropped by reopening this dialog.
  const stale = chosen.filter((id) => !known.has(id));
  const picked = chosen.filter((id) => known.has(id));

  const was = new Set(current?.source === source ? (current?.ids ?? []) : []);
  const adding = picked.filter((id) => !was.has(id)).length;
  const dropping = [...was].filter((id) => !picked.includes(id)).length;
  const changingSource = Boolean(current) && current?.source !== source;

  function labelOf(id: string): string {
    return options.find((one) => one.value === id)?.label ?? id;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DialogHeader>
          <DialogTitle>{current ? "Which firms or campaigns" : "Ask different questions by"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex gap-2" role="group" aria-label="What the paths vary by">
            {(["firm", "campaign"] as const).map((one) => (
              <Button
                key={one}
                variant={source === one ? "default" : "outline"}
                size="sm"
                aria-pressed={source === one}
                onClick={() => {
                  setSource(one);
                  // Ids do not carry between the two: a firm id means nothing to a campaign rule.
                  setChosen(current?.source === one ? (current?.ids ?? []) : []);
                }}
              >
                {one === "firm" ? "Firm" : "Campaign"}
              </Button>
            ))}
          </div>

          <p className="text-xs text-text-muted">
            {source === "firm"
              ? "One path for each firm you pick, and a last one for every other firm."
              : "A campaign is a firm, a state and a language together, so a path per campaign varies by all three at once."}
          </p>

          <MultiSelect
            label={source === "firm" ? "Firms" : "Campaigns"}
            options={options}
            value={picked}
            onChange={(next) => setChosen([...next, ...stale])}
            placeholder={source === "firm" ? "Pick the firms" : "Pick the campaigns"}
            emptyText={
              source === "firm" ? "This tenant has no firms yet." : "This tenant has no campaigns yet."
            }
          />

          {picked.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label="The paths this will make">
              {picked.map((id) => (
                <li
                  key={id}
                  className="rounded-full border border-accent-4 bg-accent-2 px-2 py-0.5 text-[11px] text-accent-11"
                >
                  {labelOf(id)}
                </li>
              ))}
            </ul>
          ) : null}

          {stale.length > 0 ? (
            <Callout tone="warning">
              {stale.length === 1 ? "One path names something" : `${stale.length} paths name things`}{" "}
              this tenant no longer lists — an archived firm or campaign. They are kept as they are;
              remove the path itself on the canvas to be rid of it.
            </Callout>
          ) : null}

          {/* What pressing the button will do, when it will do more than add. Rebuilding the
              paths is not obvious from "Make 3 paths", and it is the admin's own work that goes. */}
          {changingSource ? (
            <Callout tone="warning">
              This step varies by {current?.source === "firm" ? "firm" : "campaign"} today. Saving
              a {source} pick replaces its paths, and anything built under them goes with them.
            </Callout>
          ) : dropping > 0 ? (
            <Callout tone="warning">
              {dropping === 1 ? "One path goes" : `${dropping} paths go`}
              {adding > 0 ? `, ${adding} ${adding === 1 ? "is" : "are"} added` : ""}. Steps built
              under a path that goes are removed with it.
            </Callout>
          ) : null}

          <p className="text-xs text-text-muted">
            To vary by the state the accident happened in, ask that question and make a path per
            answer: it is where the accident was, which is not always where the client lives.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={picked.length === 0}
            onClick={() => {
              const chose = picked.map((id) => ({
                id: source === "firm" ? Number(id) : id,
                label: labelOf(id),
              }));
              // The caller closes this: closing here first would clear the state its handler
              // reads, and the pick would be dropped without a word.
              onPick(source, chose);
            }}
          >
            {current ? "Save" : `Make ${picked.length || ""} ${picked.length === 1 ? "path" : "paths"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
